import { useCallback } from "react";
import {
    MIN_RATING_ALL,
    POSTED_PERIOD_ALL,
    RADIUS_ALL,
} from "components/ListPage/filters/inlineFilterConfig";
import type { CityOption } from "components/ListPage/filters";
import {
    buildResetInlineProviderFilterSearchParams,
    buildSearchParams,
    joinCsv,
} from "../searchParams";
import type { ProviderFilterActionsOptions } from "./types";

export const useProviderFilterActions = ({
    activeFilters,
    queryValues,
    currentKeyword,
    pager,
    categories,
    selectedSubcategories,
    setAll,
    changeFetcherParams,
    setCityInput,
    setCityOptions,
}: ProviderFilterActionsOptions) => {
    const applyFilters = useCallback(
        (nextFilters: HashMap<unknown>) => {
            const params = buildSearchParams({
                activeFilters: {
                    ...activeFilters,
                    ...queryValues,
                    ...nextFilters,
                },
                keyword: currentKeyword,
                pager,
            });

            changeFetcherParams(params);
            setAll(params);
        },
        [activeFilters, changeFetcherParams, currentKeyword, pager, queryValues, setAll]
    );

    const handleCategoryChange = useCallback(
        (categoriesValue: string[]) => {
            const selectedCategoriesValueSet = new Set(categoriesValue);
            const allowedSubcategories = new Set(
                categories
                    .filter(
                        (category) =>
                            selectedCategoriesValueSet.size === 0 || selectedCategoriesValueSet.has(category.value)
                    )
                    .flatMap((category) => category.subcategories.map((subcategory) => subcategory.value))
            );

            applyFilters({
                filter_categories: joinCsv(categoriesValue),
                filter_subcategories: joinCsv(
                    selectedSubcategories.filter((subcategory) => allowedSubcategories.has(subcategory))
                ),
            });
        },
        [applyFilters, categories, selectedSubcategories]
    );

    const handleSubcategoryChange = useCallback(
        (subcategories: string[]) => applyFilters({ filter_subcategories: joinCsv(subcategories) }),
        [applyFilters]
    );

    const handleRadiusChange = useCallback(
        (value: string) =>
            applyFilters({
                filter_range: value === RADIUS_ALL ? undefined : value,
            }),
        [applyFilters]
    );

    const handlePostedPeriodChange = useCallback(
        (value: string) =>
            applyFilters({
                filter_posted_period: value === POSTED_PERIOD_ALL ? undefined : value,
            }),
        [applyFilters]
    );

    const handleMinRatingChange = useCallback(
        (value: string) =>
            applyFilters({
                filter_min_rating: value === MIN_RATING_ALL ? undefined : value,
            }),
        [applyFilters]
    );

    const handleCityInputChange = useCallback(
        (value: string) => {
            setCityInput(value);

            if (value.trim() === "") {
                setCityOptions([]);
                applyFilters({
                    filter_city: undefined,
                    filter_coordinates_lat: undefined,
                    filter_coordinates_lon: undefined,
                });
            }
        },
        [applyFilters, setCityInput, setCityOptions]
    );

    const handleCitySelect = useCallback(
        (option: CityOption) => {
            setCityInput(option.value);
            setCityOptions([]);
            applyFilters({
                filter_city: option.value,
                filter_coordinates_lat: option.lat,
                filter_coordinates_lon: option.lon,
            });
        },
        [applyFilters, setCityInput, setCityOptions]
    );

    const handleReset = useCallback(() => {
        const params = buildResetInlineProviderFilterSearchParams({
            currentQueryValues: queryValues,
            pager,
        });

        changeFetcherParams(params);
        setAll(params);
    }, [changeFetcherParams, pager, queryValues, setAll]);

    return {
        handleCategoryChange,
        handleSubcategoryChange,
        handlePostedPeriodChange,
        handleRadiusChange,
        handleMinRatingChange,
        handleCityInputChange,
        handleCitySelect,
        handleReset,
    };
};
